import PropTypes from "prop-types";
import { useRef, useState, useEffect } from "react";
import { getMyAccountBaseUrl } from "../../../helpers/externalLinks";
import { noticeError } from "@src/helpers/monitoring";
import { getFloor } from "@asosteam/asos-web-store-presentation-node";
import NewsletterForm from "./NewsletterForm";
import styles from "./NewsletterSignupForm.deferred.css";
const FORM_STATE = {
  IDLE: "idle",
  SUCCESS: "success",
  ACCOUNT_EXISTS: "accountExists",
  ERROR: "error",
  INVALID_EMAIL: "invalidEmail"
};

const isValidEmail = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const NewsletterSignupForm = ({ formatTranslation }) => {
  const [email, setEmail] = useState("");
  const [formState, setFormState] = useState(FORM_STATE.IDLE);
  const [SuccessMessage, setSuccessMessage] = useState(null);
  const [AccountExistsMessage, setAccountExistsMessage] = useState(null);
  const emailInputRef = useRef(null);
  const prevFormStateRef = useRef(formState);
  const programmaticFocusRef = useRef(false);

  useEffect(() => {
    if (
      prevFormStateRef.current === FORM_STATE.ACCOUNT_EXISTS &&
      formState === FORM_STATE.IDLE
    ) {
      emailInputRef.current?.focus();
    }
    prevFormStateRef.current = formState;
  }, [formState]);

  useEffect(() => {
    if (
      formState === FORM_STATE.ERROR ||
      formState === FORM_STATE.INVALID_EMAIL
    ) {
      programmaticFocusRef.current = true;
      emailInputRef.current?.focus();
    }
  }, [formState]);

  const handleFocus = () => {
    if (programmaticFocusRef.current) {
      programmaticFocusRef.current = false;
      return;
    }

    if (
      formState === FORM_STATE.ERROR ||
      formState === FORM_STATE.INVALID_EMAIL
    ) {
      setFormState(FORM_STATE.IDLE);
    }
  };

  const handleSubmit = async event => {
    event.preventDefault();

    if (!email.trim()) {
      setFormState(FORM_STATE.ERROR);
      return;
    }

    if (!isValidEmail(email)) {
      setFormState(FORM_STATE.INVALID_EMAIL);
      return;
    }

    try {
      const { checkEmailStatus } = await import(
        /* webpackChunkName: "newsletter-service" */ "./newsletterService"
      );
      const isAvailable = await checkEmailStatus(email);

      if (isAvailable) {
        if (!window.braze) {
          const brazeError = new Error("Braze SDK not available");
          noticeError(brazeError);
          console.error(brazeError);
          setFormState(FORM_STATE.ERROR);
          return;
        }
        const floor = getFloor();
        const brazeUser = window.braze.getUser();
        brazeUser.setEmail(email);
        brazeUser.setEmailNotificationSubscriptionType(
          window.braze.User.NotificationSubscriptionTypes.SUBSCRIBED
        );
        brazeUser.setCustomUserAttribute("asos_web_gender", floor);
        brazeUser.setCustomUserAttribute(
          "asos_marketing_newsletter_opted_in",
          true
        );
        brazeUser.setCustomUserAttribute(
          "asos_web_signup_source",
          "web_footer"
        );
        brazeUser.setCustomUserAttribute(
          "asos_web_signup_date",
          new Date().toISOString()
        );
        const { default: Comp } = await import(
          /* webpackChunkName: "newsletter-success" */ "./SuccessMessage"
        );
        setSuccessMessage(() => Comp);
        setFormState(FORM_STATE.SUCCESS);
      } else {
        const { default: Comp } = await import(
          /* webpackChunkName: "newsletter-account-exists" */ "./AccountExistsMessage"
        );
        setAccountExistsMessage(() => Comp);
        setFormState(FORM_STATE.ACCOUNT_EXISTS);
      }
    } catch {
      setFormState(FORM_STATE.ERROR);
    }
  };

  const handleOptIn = () => {
    window.location.assign(`${getMyAccountBaseUrl()}/contact-preferences`);
  };

  const renderContent = () => {
    if (formState === FORM_STATE.SUCCESS) {
      return SuccessMessage ? (
        <SuccessMessage formatTranslation={formatTranslation} />
      ) : null;
    }

    if (formState === FORM_STATE.ACCOUNT_EXISTS) {
      return AccountExistsMessage ? (
        <AccountExistsMessage
          onOptIn={handleOptIn}
          onUseDifferentEmail={() => {
            setEmail("");
            setFormState(FORM_STATE.IDLE);
          }}
          formatTranslation={formatTranslation}
        />
      ) : null;
    }

    return (
      <NewsletterForm
        email={email}
        onEmailChange={e => {
          setEmail(e.target.value);
          if (
            formState === FORM_STATE.ERROR ||
            formState === FORM_STATE.INVALID_EMAIL
          )
            setFormState(FORM_STATE.IDLE);
        }}
        onSubmit={handleSubmit}
        onFocus={handleFocus}
        hasError={
          formState === FORM_STATE.ERROR ||
          formState === FORM_STATE.INVALID_EMAIL
        }
        errorMessageKey={
          formState === FORM_STATE.INVALID_EMAIL
            ? "footer_newsletter_invalid_email_error_message"
            : "footer_newsletter_error_message"
        }
        emailInputRef={emailInputRef}
        formatTranslation={formatTranslation}
      />
    );
  };

  return (
    <section data-testid="newsletter-signup-form" className={styles.container}>
      <h2 id="newsletter-form-title" className={styles.title}>
        {formatTranslation("footer_newsletter_generic_title")}
      </h2>
      {renderContent()}
    </section>
  );
};

NewsletterSignupForm.propTypes = {
  formatTranslation: PropTypes.func.isRequired
};

export default NewsletterSignupForm;
